import { type JSX, type ReactNode, useEffect, useRef, useState } from 'react';

import { routes } from '../routes';
import { AppLink, useOptionalNavigator } from '../discovery/router';
import { apiGet } from '../session/client';
import { type SessionUser, initialsOf, useSession } from '../session/SessionProvider';
import { LoginLink } from './panels';
import { type ThemeChoice, useTheme } from './preferences';

/**
 * The website pages' header, built from `JT Site Header.dc.html`.
 *
 * A new component, used only on the website pages: the homepage keeps its own
 * `JTHeader` untouched (owner decision D1, 2026-11-01).
 *
 * Everything the frame draws is here — the wordmark, Orders and Bookings when
 * signed in, Log in when not, Help, the menu button with the person's initials,
 * and the "My account" menu with its Rewards, Prescriptions, Orders & bookings
 * and Theme views. What the frame filled with a demo person ("Ali Khan", PENNY
 * 1,240 · £12.40) comes from the real session and the real PENNY wallet, or is
 * not shown.
 *
 * Two things the frame draws are not offered, because the platform has no
 * such thing yet (owner decision D8, 2026-11-01: Rewards is the PENNY tab
 * only): the Voucher cards and Account credit drill-downs. Their tiles are
 * drawn and say "Coming soon"; they open nothing.
 */

type MenuView = 'home' | 'rewards' | 'rx' | 'orders' | 'theme';

const ICONS = {
  rewards:
    'M20 12v9H4v-9M2 7h20v5H2zM12 21V7M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7ZM12 7h4.5a2.5 2.5 0 1 0 0-5C13 2 12 7 12 7Z',
  prescriptions: 'M9 3h6v4H9zM6 5h12v16H6zM9 12h6M12 9v6',
  orders: 'M4 6h16M4 12h16M4 18h10',
  help: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 11v5M12 8h.01',
  theme: 'M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM12 2v2M12 20v2M2 12h2M20 12h2',
  driver:
    'M3 16V8h11v8M14 11h4l3 3v2h-7M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  partner: 'M3 10h18l-1.5-5h-15L3 10ZM5 10v10h14V10M10 20v-5h4v5',
  logout: 'M14 4h5v16h-5M10 8l-4 4 4 4M6 12h10',
} as const;

const GATES = {
  rx: {
    head: 'Your prescriptions, in one place',
    copy: "Log in to order repeat prescriptions and see when they're ready to collect or on their way.",
    items: [
      {
        t: 'Repeat prescriptions',
        d: 'Reorder in a few taps',
        bg: '#fff1d6',
        fg: '#b86e00',
        icon: 'M4 12a8 8 0 0 1 14-5.3M20 12a8 8 0 0 1-14 5.3M18 3v4h-4M6 21v-4h4',
      },
      {
        t: 'Track status',
        d: 'From approved to delivered',
        bg: '#ddf2e7',
        fg: '#156b49',
        icon: 'M3 16V8h11v8M14 11h4l3 3v2h-7M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
      },
    ],
  },
  orders: {
    head: "Everything you've ordered and booked",
    copy: 'Log in to track deliveries live, reorder past items and manage your appointments.',
    items: [
      {
        t: 'Orders',
        d: 'Track and reorder',
        bg: '#fff1d6',
        fg: '#b86e00',
        icon: 'M5 8h14l-1 12H6ZM9 8V6a3 3 0 0 1 6 0v2',
      },
      {
        t: 'Bookings',
        d: 'Change or cancel',
        bg: '#ddf2e7',
        fg: '#156b49',
        icon: 'M4.5 6h15v14h-15zM4.5 10h15M8.5 3.5v4M15.5 3.5v4',
      },
    ],
  },
} as const;

const THEMES: readonly {
  readonly id: ThemeChoice;
  readonly label: string;
  readonly sub: string;
  readonly sw: string;
  readonly ic: string;
  readonly icon: string;
}[] = [
  {
    id: 'light',
    label: 'Light',
    sub: 'Bright and clear',
    sw: '#ffffff',
    ic: '#5b2ede',
    icon: 'M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  },
  {
    id: 'dark',
    label: 'Dark',
    sub: 'Easier on the eyes at night',
    sw: '#17112b',
    ic: '#ffd166',
    icon: 'M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z',
  },
  {
    id: 'system',
    label: 'System',
    sub: 'Match your device setting',
    sw: 'linear-gradient(135deg,#fff 50%,#17112b 50%)',
    ic: '#5b2ede',
    icon: 'M4 5h16v11H4zM9 20h6M12 16v4',
  },
];

function Icon({
  d,
  stroke,
  size = 17,
}: {
  readonly d: string;
  readonly stroke: string;
  readonly size?: number;
}): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

/**
 * The Store frame's header row (`Just Treated Store.dc.html`): the wordmark,
 * the location picker and — once the basket holds something — the basket
 * pill, then the menu. It has no Orders, Bookings, Log in or Help links; the
 * menu carries all of those, as the frame's does.
 */
export interface StoreHeaderParts {
  readonly location: ReactNode;
  readonly basket?: ReactNode;
}

export function SiteHeader({ store }: { readonly store?: StoreHeaderParts } = {}): JSX.Element {
  const session = useSession();
  const [theme, setTheme] = useTheme();
  const [open, setOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const signedIn = session.status === 'signedIn' && session.user !== undefined;
  const signedOut = session.status === 'signedOut' || session.status === 'unreachable';

  const close = (): void => {
    setOpen(false);
    menuButton.current?.focus();
  };

  return (
    <>
      <header className={`jt-site-header${store !== undefined ? ' jt-site-header--store' : ''}`}>
        <div className="jt-site-header__inner">
          <AppLink to={routes.home.path} className="jt-site-header__brand">
            Just Treated
          </AppLink>
          {store !== undefined ? store.location : null}
          <div className="jt-site-header__nav">
            {store !== undefined ? store.basket : null}
            {signedIn && store === undefined ? (
              <>
                <AppLink to={routes.orders.path} className="jt-site-header__link jt-site-header__link--wide">
                  Orders
                </AppLink>
                <AppLink
                  to={routes.bookings.path}
                  className="jt-site-header__link jt-site-header__link--wide"
                >
                  Bookings
                </AppLink>
              </>
            ) : null}
            {signedOut && store === undefined ? (
              <LoginLink className="jt-site-header__link">Log in</LoginLink>
            ) : null}
            {store === undefined ? (
              <AppLink to={routes.helpCentre.path} className="jt-site-header__help" ariaLabel="Help">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#5b2ede"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M9.6 9.3a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .8-1 1.5v.4M12 16.8h.01" />
                </svg>
                Help
              </AppLink>
            ) : null}
            <button
              ref={menuButton}
              type="button"
              aria-label="Menu"
              aria-haspopup="dialog"
              aria-expanded={open}
              className={`jt-site-header__menu ${signedIn ? 'jt-site-header__menu--signed' : ''}`}
              onClick={() => setOpen(true)}
            >
              {signedIn && session.user !== undefined ? (
                <span className="jt-site-header__initials" aria-hidden="true">
                  {initialsOf(session.user)}
                </span>
              ) : null}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2.2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M5 7h14M5 12h14M5 17h9" />
              </svg>
            </button>
          </div>
        </div>
      </header>
      {open ? (
        <AccountMenu
          user={signedIn ? session.user : undefined}
          theme={theme}
          onTheme={setTheme}
          onSignOut={async () => {
            await session.signOut().catch(() => undefined);
            close();
          }}
          onClose={close}
        />
      ) : null}
    </>
  );
}

/** The real PENNY balance, asked for only when the signed-in menu opens. Hidden if it cannot be read. */
function usePennyBalance(user: SessionUser | undefined): string | undefined {
  const [label, setLabel] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (user === undefined) return undefined;
    let live = true;
    apiGet<{ availableUnits?: number; valueMinorPerUnit?: number | null; currency?: string | null }>(
      '/consumer/penny',
    )
      .then((wallet) => {
        if (!live || typeof wallet.availableUnits !== 'number') return;
        const units = wallet.availableUnits.toLocaleString('en-GB');
        const value =
          typeof wallet.valueMinorPerUnit === 'number' && typeof wallet.currency === 'string'
            ? ` · ${new Intl.NumberFormat('en-GB', { style: 'currency', currency: wallet.currency }).format(
                (wallet.availableUnits * wallet.valueMinorPerUnit) / 100,
              )}`
            : '';
        setLabel(`${units}${value}`);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [user]);

  return label;
}

function AccountMenu({
  user,
  theme,
  onTheme,
  onSignOut,
  onClose,
}: {
  readonly user: SessionUser | undefined;
  readonly theme: ThemeChoice;
  readonly onTheme: (choice: ThemeChoice) => void;
  readonly onSignOut: () => Promise<void>;
  readonly onClose: () => void;
}): JSX.Element {
  const [view, setView] = useState<MenuView>('home');
  const navigator = useOptionalNavigator();
  const dialog = useRef<HTMLDivElement>(null);
  const penny = usePennyBalance(user);
  const signedIn = user !== undefined;

  useEffect(() => {
    dialog.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const go = (path: string): void => {
    onClose();
    navigator?.navigate(path);
  };

  const title = view === 'theme' ? 'Theme' : view === 'home' ? 'My account' : '';
  const themeLabel = THEMES.find((option) => option.id === theme)?.label ?? 'System';

  const main: readonly { label: string; icon: string; meta?: string; act: () => void }[] = [
    {
      label: 'Rewards',
      icon: ICONS.rewards,
      act: () => (signedIn ? go(routes.rewards.path) : setView('rewards')),
    },
    {
      label: 'Prescriptions',
      icon: ICONS.prescriptions,
      act: () => (signedIn ? go(routes.prescriptions.path) : setView('rx')),
    },
    {
      label: 'Orders & bookings',
      icon: ICONS.orders,
      act: () => (signedIn ? go(routes.orders.path) : setView('orders')),
    },
    { label: 'Need help?', icon: ICONS.help, act: () => go(routes.helpCentre.path) },
    { label: 'Theme', icon: ICONS.theme, meta: themeLabel, act: () => setView('theme') },
  ];

  return (
    <>
      <div className="jt-site-menu__scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label="My account"
        tabIndex={-1}
        className="jt-site-menu"
      >
        <div className="jt-site-menu__top">
          <span className="jt-site-menu__titlebar">
            {view !== 'home' ? (
              <button
                type="button"
                className="jt-site-menu__round"
                aria-label="Back to My account"
                onClick={() => setView('home')}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#17112b"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M14.6 5.4 8 12l6.6 6.6" />
                </svg>
              </button>
            ) : null}
            <span className="jt-site-menu__title">{title}</span>
          </span>
          <button type="button" className="jt-site-menu__round" aria-label="Close menu" onClick={onClose}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#17112b"
              strokeWidth="2.4"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        {view === 'home' ? (
          <>
            {user !== undefined ? (
              <AppLink to={routes.account.path} className="jt-site-menu__account">
                <span className="jt-site-menu__avatar" aria-hidden="true">
                  {initialsOf(user)}
                </span>
                <span className="jt-site-menu__who">
                  <span className="jt-site-menu__name">{user.displayName ?? user.email}</span>
                  {user.displayName !== undefined ? (
                    <span className="jt-site-menu__email">{user.email}</span>
                  ) : null}
                </span>
                {penny !== undefined ? (
                  <span className="jt-site-menu__penny">
                    <span className="jt-site-menu__penny-label">PENNY</span>
                    <span className="jt-site-menu__penny-value">{penny}</span>
                  </span>
                ) : null}
              </AppLink>
            ) : (
              <div className="jt-site-menu__join">
                <div className="jt-site-menu__join-text">
                  <span className="jt-site-menu__join-head">Join Just Treated</span>
                  <div className="jt-site-menu__join-list">
                    {[
                      'Manage repeat prescriptions',
                      'Book consultations faster',
                      'Track deliveries live',
                    ].map((line) => (
                      <span key={line}>
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#5b2ede"
                          strokeWidth="2.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="m5 12.5 4.2 4.2L19 7" />
                        </svg>
                        {line}
                      </span>
                    ))}
                  </div>
                  <span onClickCapture={onClose} className="jt-site-contents">
                    <LoginLink className="jt-site-menu__join-cta">Log in or sign up</LoginLink>
                  </span>
                </div>
                <div role="img" aria-label="Just Treated delivery bag" className="jt-site-menu__join-art" />
              </div>
            )}

            <div className="jt-site-menu__list">
              {main.map((item) => (
                <MenuRow
                  key={item.label}
                  icon={item.icon}
                  label={item.label}
                  meta={item.meta}
                  onClick={item.act}
                />
              ))}
            </div>
            <div className="jt-site-menu__rule" />
            <div className="jt-site-menu__list">
              <MenuRow
                icon={ICONS.driver}
                label="Become a driver"
                muted
                onClick={() => go(routes.couriers.path)}
              />
              <MenuRow
                icon={ICONS.partner}
                label="Partner with us"
                muted
                onClick={() => go(routes.partnersWithUs.path)}
              />
            </div>
            {signedIn ? (
              <>
                <div className="jt-site-menu__rule" />
                <button
                  type="button"
                  className="jt-site-menu__row jt-site-menu__row--danger"
                  onClick={() => void onSignOut()}
                >
                  <span className="jt-site-menu__row-icon jt-site-menu__row-icon--danger">
                    <Icon d={ICONS.logout} stroke="#a81f1f" />
                  </span>
                  <span className="jt-site-menu__row-label">Log out</span>
                </button>
              </>
            ) : null}
          </>
        ) : null}

        {view === 'rewards' ? (
          <>
            <span className="jt-site-menu__big">Rewards</span>
            <MenuGate
              head="Unlock and save"
              copy="Log in to see your PENNY balance and how you earn it."
              onLogin={onClose}
            />
            <div className="jt-site-menu__tiles">
              <ComingTile
                label="Voucher cards"
                bg="#fff1d6"
                fg="#b86e00"
                icon="M3 7h18v3a2 2 0 0 0 0 4v3H3v-3a2 2 0 0 0 0-4ZM14 7v2M14 11v2M14 15v2"
              />
              <ComingTile
                label="Account credit"
                bg="#ddf2e7"
                fg="#156b49"
                icon="M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17ZM14.5 9.2c-.5-.8-1.4-1.2-2.5-1.2-1.5 0-2.5.8-2.5 2s1 1.6 2.5 2 2.5.8 2.5 2-1 2-2.5 2c-1.1 0-2-.4-2.5-1.2M12 6.5V8M12 16v1.5"
              />
            </div>
          </>
        ) : null}

        {view === 'rx' || view === 'orders' ? (
          <>
            <span className="jt-site-menu__big">{view === 'rx' ? 'Prescriptions' : 'Orders & bookings'}</span>
            <MenuGate head={GATES[view].head} copy={GATES[view].copy} onLogin={onClose} />
            <div className="jt-site-menu__tiles">
              {GATES[view].items.map((item) => (
                <div key={item.t} className="jt-site-menu__tile">
                  <span className="jt-site-menu__tile-text">
                    <span className="jt-site-menu__tile-head">{item.t}</span>
                    <span className="jt-site-menu__tile-sub">{item.d}</span>
                  </span>
                  <span
                    className="jt-site-menu__tile-icon"
                    style={{ background: item.bg }}
                    aria-hidden="true"
                  >
                    <Icon d={item.icon} stroke={item.fg} size={22} />
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : null}

        {view === 'theme' ? (
          <div role="radiogroup" aria-label="Theme" className="jt-site-menu__themes">
            {THEMES.map((option) => (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={theme === option.id}
                className="jt-site-menu__theme"
                onClick={() => onTheme(option.id)}
              >
                <span className="jt-site-menu__swatch" style={{ background: option.sw }} data-keep="1">
                  <Icon d={option.icon} stroke={option.ic} size={18} />
                </span>
                <span className="jt-site-menu__theme-text">
                  <span className={`jt-site-menu__theme-label ${theme === option.id ? 'is-on' : ''}`}>
                    {option.label}
                  </span>
                  <span className="jt-site-menu__theme-sub">{option.sub}</span>
                </span>
                {theme === option.id ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#5b2ede"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m5 12.5 4.2 4.2L19 7" />
                  </svg>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
}

function MenuRow({
  icon,
  label,
  meta,
  muted = false,
  onClick,
}: {
  readonly icon: string;
  readonly label: string;
  readonly meta?: string;
  readonly muted?: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  return (
    <button type="button" className="jt-site-menu__row" onClick={onClick}>
      <span className="jt-site-menu__row-icon">
        <Icon d={icon} stroke={muted ? '#55506b' : '#5b2ede'} />
      </span>
      <span className="jt-site-menu__row-label">{label}</span>
      {meta !== undefined ? <span className="jt-site-menu__row-meta">{meta}</span> : null}
    </button>
  );
}

function MenuGate({
  head,
  copy,
  onLogin,
}: {
  readonly head: string;
  readonly copy: string;
  readonly onLogin: () => void;
}): ReactNode {
  return (
    <div className="jt-site-card jt-site-card--menu">
      <div className="jt-site-card__row">
        <span className="jt-site-card__text">
          <span className="jt-site-card__head">{head}</span>
          <span className="jt-site-card__copy">{copy}</span>
        </span>
        <span className="jt-site-card__icon" aria-hidden="true">
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 11h14v10H5zM8 11V8a4 4 0 0 1 7.5-2" />
            <path d="M12 15v2" />
          </svg>
        </span>
      </div>
      <div className="jt-site-card__actions" onClickCapture={onLogin}>
        <LoginLink className="jt-site-pill jt-site-pill--white">Create an account</LoginLink>
        <LoginLink className="jt-site-pill jt-site-pill--ink">Log in</LoginLink>
      </div>
    </div>
  );
}

/** A tile the frame draws for something the platform does not have yet. Says so; opens nothing. */
function ComingTile({
  label,
  bg,
  fg,
  icon,
}: {
  readonly label: string;
  readonly bg: string;
  readonly fg: string;
  readonly icon: string;
}): JSX.Element {
  return (
    <div className="jt-site-menu__tile" aria-disabled="true">
      <span className="jt-site-menu__tile-text">
        <span className="jt-site-menu__tile-head">{label}</span>
        <span className="jt-site-menu__tile-sub">Coming soon</span>
      </span>
      <span className="jt-site-menu__tile-icon" style={{ background: bg }} aria-hidden="true">
        <Icon d={icon} stroke={fg} size={22} />
      </span>
    </div>
  );
}
